import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import CreateCampaign from "@/pages/CreateCampaign";
import CampaignResults from "@/pages/CampaignResults";
import CampaignLibrary from "@/pages/CampaignLibrary";
import DealershipProfile from "@/pages/DealershipProfile";
import Settings from "@/pages/Settings";
import DealerIntelligenceProfile from "@/pages/DealerIntelligenceProfile";
import Opportunities from "@/pages/Opportunities";
import BuyerIntelligence from "@/pages/BuyerIntelligence";
import PersonaDetail from "@/pages/PersonaDetail";
import Calendar from "@/pages/Calendar";
import Landing from "@/pages/Landing";
import AdminRoute from "@/components/AdminRoute";
import AdminLayout from "@/components/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminDemoRequests from "@/pages/admin/AdminDemoRequests";
import AdminDemoRequestDetail from "@/pages/admin/AdminDemoRequestDetail";
import AdminAssessments from "@/pages/admin/AdminAssessments";
import AdminAssessmentDetail from "@/pages/admin/AdminAssessmentDetail";
import AdminDealers from "@/pages/admin/AdminDealers";
import AdminDealerDetail from "@/pages/admin/AdminDealerDetail";
import AdminUsage from "@/pages/admin/AdminUsage";
import AdminUsageDetail from "@/pages/admin/AdminUsageDetail";
import AdminIntelligence from "@/pages/admin/AdminIntelligence";
import AdminPremierPartners from "@/pages/admin/AdminPremierPartners";
import AdminDealerGroups from "@/pages/admin/AdminDealerGroups";
import ExecutiveView from "@/pages/ExecutiveView";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import AdminInventorySync from "@/pages/admin/AdminInventorySync";
import TermsOfService from "@/pages/TermsOfService";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/create-campaign" element={<CreateCampaign />} />
        <Route path="/campaign-results" element={<CampaignResults />} />
        <Route path="/campaigns" element={<CampaignLibrary />} />
        <Route path="/opportunities" element={<Opportunities />} />
        <Route path="/buyer-intelligence" element={<BuyerIntelligence />} />
        <Route path="/buyer-intelligence/personas/:id" element={<PersonaDetail />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/profile" element={<DealershipProfile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/dealer-profile" element={<DealerIntelligenceProfile />} />
      </Route>
      <Route path="/executive" element={<ProtectedRoute><ExecutiveView /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminOverview />} />
        <Route path="demo-requests" element={<AdminDemoRequests />} />
        <Route path="demo-requests/:id" element={<AdminDemoRequestDetail />} />
        <Route path="assessments" element={<AdminAssessments />} />
        <Route path="assessments/:id" element={<AdminAssessmentDetail />} />
        <Route path="dealers" element={<AdminDealers />} />
        <Route path="dealers/:id" element={<AdminDealerDetail />} />
        <Route path="dealer-groups" element={<AdminDealerGroups />} />
        <Route path="usage" element={<AdminUsage />} />
        <Route path="inventory-sync" element={<AdminInventorySync />} />
        <Route path="usage/dealers/:id" element={<AdminUsageDetail />} />
        <Route path="intelligence" element={<AdminIntelligence />} />
        <Route path="premier-partners" element={<AdminPremierPartners />} />
      </Route>
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
